export function HiddenCostsOfRemodeling() {
  const hiddenCosts = [
    {
      icon: 'ri-time-line',
      title: 'Opportunity Cost of Time',
      description: 'Every month spent renovating is a month you could have had cash in hand earning returns elsewhere.',
      example: '$1.2M invested for 18 months at 8% return = $144,000 in lost investment income',
      impact: 'High'
    },
    {
      icon: 'ri-user-unfollow-line',
      title: 'Tenant Turnover & Vacancy',
      description: 'Renovations often require relocating tenants, leading to extended vacancy periods and lost rental income.',
      example: '20 units vacant for 12 months at $800/month = $192,000 in lost rent',
      impact: 'Very High'
    },
    {
      icon: 'ri-alert-line',
      title: 'Cost Overruns & Surprises',
      description: 'Construction projects routinely exceed budgets by 20-40% due to unforeseen issues discovered during work.',
      example: '$740,000 renovation budget + 30% overrun = $222,000 additional unexpected costs',
      impact: 'Very High'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Timeline Delays',
      description: 'Permit delays, contractor scheduling, material shortages, and weather can extend projects by months.',
      example: '6-month delay × $15,000/month carrying costs = $90,000 additional expense',
      impact: 'High'
    },
    {
      icon: 'ri-file-list-line',
      title: 'Permits & Compliance',
      description: 'Building permits, inspections, and code compliance upgrades add significant costs and delays.',
      example: 'Permit fees, inspection costs, code upgrades = $15,000-$50,000',
      impact: 'Medium'
    },
    {
      icon: 'ri-shield-cross-line',
      title: 'Insurance & Liability',
      description: 'Higher insurance premiums during construction and liability exposure if accidents occur on site.',
      example: 'Increased premiums + liability coverage = $5,000-$15,000',
      impact: 'Medium'
    },
    {
      icon: 'ri-mental-health-line',
      title: 'Stress & Health Impact',
      description: 'Managing contractors, dealing with problems, and the mental burden of a major project takes a toll.',
      example: 'Countless hours of stress, lost sleep, and reduced quality of life',
      impact: 'Severe'
    },
    {
      icon: 'ri-exchange-dollar-line',
      title: 'Financing Costs',
      description: 'Interest on construction loans or opportunity cost of using your own capital during the renovation period.',
      example: '$740,000 at 8% interest for 12 months = $59,200 in interest costs',
      impact: 'High'
    },
    {
      icon: 'ri-tools-line',
      title: 'Property Management During Construction',
      description: 'Coordinating with tenants, managing access, handling complaints, and overseeing the project.',
      example: 'Additional management time and fees = $3,000-$10,000/month',
      impact: 'Medium'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-red-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The Hidden Costs Nobody Talks About
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Beyond the obvious renovation expenses, there are numerous hidden costs that can devastate your return on investment. Most owners don't discover these until it's too late.
          </p>
        </div>

        {/* Hidden Costs Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {hiddenCosts.map((cost, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-red-100">
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className={`${cost.icon} text-2xl text-white`}></i>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  cost.impact === 'Very High' ? 'bg-red-600 text-white' :
                  cost.impact === 'High' ? 'bg-red-500 text-white' :
                  cost.impact === 'Severe' ? 'bg-red-700 text-white' :
                  'bg-red-400 text-white'
                }`}>
                  {cost.impact}
                </span>
              </div>
              <h3 className="text-xl font-bold mb-3">{cost.title}</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">{cost.description}</p>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="text-xs font-bold text-red-700 mb-2">Real Example:</div>
                <div className="text-sm text-gray-700">{cost.example}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Hidden Costs Calculation */}
        <div className="bg-white rounded-2xl p-10 shadow-xl mb-12 border-2 border-red-300">
          <h3 className="text-3xl font-bold mb-8 text-center">Adding Up the Hidden Costs</h3>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-lg text-gray-700">Lost investment income (opportunity cost)</span>
                <strong className="text-xl text-red-600">$144,000</strong>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-lg text-gray-700">Lost rental income during vacancy</span>
                <strong className="text-xl text-red-600">$192,000</strong>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-lg text-gray-700">Cost overruns (30% of budget)</span>
                <strong className="text-xl text-red-600">$222,000</strong>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-lg text-gray-700">Timeline delay carrying costs</span>
                <strong className="text-xl text-red-600">$90,000</strong>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-lg text-gray-700">Financing costs</span>
                <strong className="text-xl text-red-600">$59,200</strong>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-lg text-gray-700">Permits, insurance, management</span>
                <strong className="text-xl text-red-600">$75,000</strong>
              </div>
              <div className="flex justify-between items-center pt-6 border-t-2 border-red-400">
                <span className="text-2xl font-bold">Total Hidden Costs</span>
                <strong className="text-3xl text-red-600">$782,200</strong>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-red-50 rounded-xl p-8 text-center">
              <p className="text-2xl font-bold text-gray-800 mb-4">
                Combined with $1.2M in direct renovation costs...
              </p>
              <p className="text-4xl font-bold text-red-700">
                Your total investment is nearly $2 MILLION
              </p>
              <p className="text-lg text-gray-700 mt-4">
                And remember, the property value only increased by $225,000 in our earlier example!
              </p>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/60921cb180f049723878be8c100717e7.jpg"
            alt="Hidden costs of apartment building renovation"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}